/**
 * Security Panel Component
 *
 * Security settings: password status and change password flow
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ChangePasswordModal } from './ChangePasswordModal';
import { accountService } from '../../services/accountService';

export function SecurityPanel() {
  const { t } = useTranslation();
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [passwordStatus, setPasswordStatus] = useState<{
    lastPasswordChange: string | null;
    passwordStrength: string;
  } | null>(null);

  useEffect(() => {
    const loadSecurityStatus = async () => {
      try {
        const status = await accountService.getSecurityStatus();
        setPasswordStatus(status);
      } catch (error) {
        console.error('Failed to load security status:', error);
      }
    };
    loadSecurityStatus();
  }, []);

  const formatLastChange = (date: string | null) => {
    if (!date) return t('account.security.lastUpdate.never');
    const changeDate = new Date(date);
    const now = new Date();
    const monthsDiff = Math.floor(
      (now.getTime() - changeDate.getTime()) / (1000 * 60 * 60 * 24 * 30)
    );
    if (monthsDiff === 0) return t('account.security.lastUpdate.recent');
    return t('account.security.lastUpdate.monthsAgo', { count: monthsDiff });
  };

  return (
    <>
      <section className='bg-surface-light dark:bg-surface-dark rounded-xl border border-border-light dark:border-border-dark p-6 shadow-sm flex flex-col h-full'>
        <h3 className='text-lg font-bold text-text-main dark:text-text-main-dark flex items-center gap-2 mb-6'>
          <span className='material-symbols-outlined text-primary'>shield</span>
          {t('account.security.title')}
        </h3>

        <div className='space-y-4 flex-1'>
          <div className='rounded-lg bg-background-light dark:bg-background-dark p-4 border border-border-light dark:border-border-dark'>
            <div className='flex items-start gap-3'>
              <span className='material-symbols-outlined text-green-600 dark:text-green-400 mt-1'>
                check_circle
              </span>
              <div>
                <p className='text-sm font-bold text-text-main dark:text-text-main-dark'>
                  {t('account.security.passwordStrength')}
                </p>
                <p className='text-xs text-text-secondary dark:text-text-secondary-dark mt-1'>
                  {formatLastChange(passwordStatus?.lastPasswordChange || null)}
                </p>
              </div>
            </div>
          </div>

          <div className='space-y-3 pt-2'>
            <button
              onClick={() => setShowChangePassword(true)}
              className='w-full flex items-center justify-between p-3 rounded-lg border border-border-light dark:border-border-dark hover:bg-background-light dark:hover:bg-background-dark text-left transition-colors group focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2'
            >
              <span className='text-sm font-medium text-text-main dark:text-text-main-dark'>
                {t('account.security.changePassword')}
              </span>
              <span className='material-symbols-outlined text-text-secondary group-hover:text-primary transition-colors'>
                arrow_forward
              </span>
            </button>
          </div>
        </div>
      </section>

      {showChangePassword && (
        <ChangePasswordModal
          onClose={() => setShowChangePassword(false)}
          onSuccess={async () => {
            setShowChangePassword(false);
            // Refresh security status
            try {
              const status = await accountService.getSecurityStatus();
              setPasswordStatus(status);
            } catch (error) {
              console.error('Failed to refresh security status:', error);
            }
          }}
        />
      )}
    </>
  );
}
